import React from 'react';
import { faDesktop } from '@fortawesome/pro-regular-svg-icons/faDesktop';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GetStaticPaths, GetStaticProps } from 'next';
import useI18n, { getLocalePaths } from '@i18n';

import Step from '@pages/plugin/Step';
import Browsers from '@pages/plugin/Browsers';
import Screenshot from '@pages/plugin/Screen';

import WithRole, { rolesEnum } from '@HOCs/WithRole';

import RU1 from '@pages/plugin/images/ru_1.jpg';
import RU2 from '@pages/plugin/images/ru_2.jpg';
import RU3 from '@pages/plugin/images/ru_3.jpg';
import EN1 from '@pages/plugin/images/en_1.jpg';
import EN2 from '@pages/plugin/images/en_2.jpg';
import EN3 from '@pages/plugin/images/en_3.jpg';
import { CustomGetStaticPathsResult, CustomGetStaticPropsResult } from '../../../../types';

const screens = {
    ru: {
        1: RU1,
        2: RU2,
        3: RU3,
    },
    en: {
        1: EN1,
        2: EN2,
        3: EN3,
    },
};

const PluginPage: React.FC = () => {
    const { t, locale } = useI18n('Plugins');
    const i18nScreens = screens[locale];

    const config = [
        {
            id: 1,
            title: 'Download the extension for your browser',
            body: <Browsers />,
        },
        {
            id: 2,
            title: 'Log in to the plugin',
            body: <Screenshot src={i18nScreens[1]} />,
        },
        {
            id: 3,
            title: 'To get an affiliate link, just be on the offer page of interest',
            subTitle: 'If creating an affiliate link to the page is possible',
            body: <Screenshot src={i18nScreens[2]} />,
            detailedInfo:
                'If you specify a name for the creative, a new deeplink will be created under this name',
        },
        {
            id: 4,
            title: 'For AliExpress product pages, the plugin will show product affiliation',
            body: <Screenshot src={i18nScreens[3]} />,
        },
    ];

    return (
        <>
            <h3 className="mb-10 font-medium text-h1 xs:mb-6">{t('Browser Extensions')}</h3>

            <div className="hidden xs:block">
                <div>
                    <p className="mb-5 text-blueGray-500">
                        {t(
                            'The plugin allows you to receive an affiliate link to any open page in the browser',
                        )}
                    </p>

                    <div className="mb-5 h-0.5 bg-blueGray-200" />
                </div>

                {config.map(item => {
                    return (
                        <Step
                            key={item.id}
                            count={item.id}
                            title={t(item.title)}
                            subTitle={t(item.subTitle)}
                            detailedInfo={t(item.detailedInfo)}
                        >
                            {item.body}
                        </Step>
                    );
                })}
            </div>

            <div className="flex flex-col items-center text-center mb-14 xs:hidden">
                <FontAwesomeIcon className="mb-5 text-red-400" icon={faDesktop} size="8x" />

                <span className="font-medium text-black text-h2">{t('WeHaveMadePlugin')}</span>
                <span className="text-gray-400 text-p1 max-w-3/4">{t('UseComputer')}</span>
            </div>
        </>
    );
};

export default WithRole(PluginPage, [rolesEnum.user]);

export const getStaticProps: GetStaticProps = async ({
    params,
}): Promise<CustomGetStaticPropsResult> => {
    return { props: { locale: params?.locale } };
};

export const getStaticPaths: GetStaticPaths = async (): Promise<CustomGetStaticPathsResult> => {
    return { paths: getLocalePaths(), fallback: false };
};
